import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Paper,
  Avatar,
  IconButton,
  Button,
  TextField,
  Tabs,
  Tab,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  InputAdornment,
  Skeleton
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ChatBubbleOutline as CommentIcon,
  Share as ShareIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Image as ImageIcon,
  Send as SendIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

// Import the centralized API client that has proper authentication
import { socialAPI } from '../utils/api';
import SuggestedUsers from '../components/SuggestedUsers';
import { useNavigate } from 'react-router-dom';

const SocialFeed = () => {
  const [tabValue, setTabValue] = useState(0);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createPostOpen, setCreatePostOpen] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostImage, setNewPostImage] = useState(null);
  const [newPostImagePreview, setNewPostImagePreview] = useState(null);
  const [submittingPost, setSubmittingPost] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [followingUsers, setFollowingUsers] = useState({});
  const navigate = useNavigate();

  // Refs for cleanup and race condition prevention
  const abortControllerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Load feed data with race condition prevention
  const loadFeed = async (feedType = 'feed') => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      const response = feedType === 'discover'
        ? await socialAPI.getDiscoverFeed()
        : await socialAPI.getFeed();

      // Only update state if this request wasn't aborted
      if (!controller.signal.aborted) {
        setPosts(response.data.posts || []);
      }
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        console.log('Feed request aborted');
        return;
      }

      console.error('Error loading feed:', err);

      // Only update state if this request wasn't aborted
      if (!controller.signal.aborted) {
        // Handle authentication errors specifically
        if (err.response?.status === 401) {
          setError('You need to be logged in to view the social feed');
        } else {
          setError('Failed to load social feed');
        }
      }
    } finally {
      // Only update loading state if this request wasn't aborted
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const feedType = tabValue === 0 ? 'feed' : 'discover';
    loadFeed(feedType);
  }, [tabValue]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle like toggle
  const handleLike = async (postId) => {
    try {
      const response = await socialAPI.likePost(postId);
      
      // Update the post in the state
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId 
            ? { 
                ...post, 
                user_liked: response.data.liked,
                likes_count: response.data.likes_count 
              }
            : post
        )
      );
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  // Handle comment submission
  const handleComment = async (postId, commentContent) => {
    if (!commentContent.trim()) return;

    try {
      const response = await socialAPI.addComment(postId, commentContent);

      // Update the post with the new comment
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === postId
            ? {
                ...post,
                comments: [...post.comments, response.data.comment],
                comments_count: post.comments_count + 1
              }
            : post
        )
      );
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  // Handle post creation
  const handleCreatePost = async () => {
    if (!newPostContent.trim() && !newPostImage) {
      setError('Post must have content or image');
      return;
    }

    try {
      setSubmittingPost(true);
      const formData = new FormData();
      formData.append('content', newPostContent);
      if (newPostImage) {
        formData.append('image', newPostImage);
      }

      await socialAPI.createPost(formData);

      // Reset form and reload feed
      setNewPostContent('');
      setNewPostImage(null);
      setNewPostImagePreview(null);
      setCreatePostOpen(false);
      loadFeed(tabValue === 0 ? 'feed' : 'discover');
    } catch (err) {
      console.error('Error creating post:', err);
      setError('Failed to create post');
    } finally {
      setSubmittingPost(false);
    }
  };

  // Handle image selection
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewPostImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setNewPostImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Debounced search users (300ms delay)
  const searchUsers = useCallback((query) => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    // Show loading immediately
    setSearchLoading(true);

    // Debounce the actual search
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setError(null);
        const response = await socialAPI.searchUsers(query);
        setSearchResults(response.data.users || []);
      } catch (err) {
        console.error('Error searching users:', err);
        setError('Failed to search users. Please try again.');
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel pending feed request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Clear search timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle follow toggle
  const handleFollow = async (userId) => {
    if (followingUsers[userId]) return;

    try {
      setFollowingUsers(prev => ({ ...prev, [userId]: true }));
      setError(null);
      const response = await socialAPI.followUser(userId);
      
      // Update search results
      setSearchResults(prevResults =>
        prevResults.map(user =>
          user.id === userId
            ? { ...user, is_following: response.data.following }
            : user
        )
      );
    } catch (err) {
      console.error('Error toggling follow:', err);
      setError('Failed to update follow status. Please try again.');
    } finally {
      setFollowingUsers(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Post component
  const PostCard = ({ post }) => {
    const [commentText, setCommentText] = useState('');
    const [showComments, setShowComments] = useState(false);

    return (
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 16px 28px rgba(15, 29, 55, 0.1)',
            borderColor: '#c0d3f1',
          },
        }}
      >
        {/* Post Header */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
          <Avatar 
            src={post.user.avatar_url} 
            sx={{ 
              mr: 2, 
              cursor: 'pointer' 
            }}
            onClick={() => navigate(`/profile/${post.user.id}`)}
          >
            {post.user.username[0].toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography 
              variant="subtitle1" 
              fontWeight="bold"
              sx={{ 
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={() => navigate(`/profile/${post.user.id}`)}
            >
              {post.user.username}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(post.created_at))} ago
            </Typography>
          </Box>
        </Box>

        {/* Post Content */}
        {post.content && (
          <CardContent sx={{ pt: 0 }}>
            <Typography variant="body1">
              {post.content}
            </Typography>
          </CardContent>
        )}

        {/* Post Image */}
        {post.image_url && (
          <CardMedia
            component="img"
            image={post.image_url}
            alt="Post image"
            sx={{ maxHeight: 600, objectFit: 'cover' }}
          />
        )}

        {/* Outfit Items */}
        {post.outfit_items && post.outfit_items.length > 0 && (
          <Box sx={{ p: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Outfit items:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {post.outfit_items.map((itemId, index) => (
                <Chip
                  key={index}
                  label={`Item ${itemId}`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Post Actions */}
        <Box sx={{ p: 2, pt: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <IconButton onClick={() => handleLike(post.id)} color={post.user_liked ? "error" : "default"}>
              {post.user_liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            </IconButton>
            <IconButton onClick={() => setShowComments(!showComments)}>
              <CommentIcon />
            </IconButton>
            <IconButton>
              <ShareIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {post.likes_count} {post.likes_count === 1 ? 'like' : 'likes'}
          </Typography>

          {/* Comments Section */}
          {showComments && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              {post.comments.map((comment) => (
                <Box key={comment.id} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>{comment.user.username}</strong> {comment.content}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(new Date(comment.created_at))} ago
                  </Typography>
                </Box>
              ))}
              
              {/* Add Comment */}
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={() => {
                            handleComment(post.id, commentText);
                            setCommentText('');
                          }}
                          disabled={!commentText.trim()}
                        >
                          <SendIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 2,
            borderRadius: 3,
            background:
              'radial-gradient(280px 160px at 92% -20%, rgba(255, 110, 77, 0.2), transparent 70%), linear-gradient(135deg, rgba(14, 123, 255, 0.14), rgba(0, 165, 255, 0.08))',
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight={800}>
            Social Feed
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Keep up with followers, discover looks, and share outfit ideas with the community.
          </Typography>
        </Paper>
        
        {/* Tabs */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Following" />
            <Tab label="Discover" />
          </Tabs>

          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Search users to follow..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              searchUsers(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Search Results */}
          {(searchResults.length > 0 || searchLoading) && (
            <Card sx={{ mb: 1, borderRadius: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    Users
                  </Typography>
                  {searchLoading && <CircularProgress size={20} />}
                </Box>
                {searchLoading && searchResults.length === 0 ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <Typography variant="body2" color="text.secondary">
                      Searching users...
                    </Typography>
                  </Box>
                ) : (
                  searchResults.map((searchUser) => (
                  <Box
                    key={searchUser.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                      p: 1,
                      borderRadius: 2,
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(14, 123, 255, 0.06)',
                      },
                    }}
                  >
                    <Avatar 
                      src={searchUser.avatar_url} 
                      sx={{ 
                        mr: 2, 
                        cursor: 'pointer' 
                      }}
                      onClick={() => navigate(`/profile/${searchUser.id}`)}
                    >
                      {!searchUser.avatar_url && (searchUser.username?.[0]?.toUpperCase() || <PersonIcon />)}
                    </Avatar>
                    <Typography 
                      sx={{ 
                        flexGrow: 1, 
                        cursor: 'pointer' 
                      }}
                      onClick={() => navigate(`/profile/${searchUser.id}`)}
                    >
                      {searchUser.username}
                    </Typography>
                    <Button
                      variant={searchUser.is_following ? "outlined" : "contained"}
                      size="small"
                      onClick={() => handleFollow(searchUser.id)}
                      disabled={followingUsers[searchUser.id]}
                    >
                      {followingUsers[searchUser.id] ? (
                        <CircularProgress size={16} />
                      ) : (
                        searchUser.is_following ? 'Unfollow' : 'Follow'
                      )}
                    </Button>
                  </Box>
                  ))
                )}
              </CardContent>
            </Card>
          )}
        </Paper>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Posts Feed */}
      {loading ? (
        <Box>
          {[1, 2, 3].map((i) => (
            <Card
              key={i}
              variant="outlined"
              sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}
            >
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="30%" />
                  <Skeleton variant="text" width="20%" />
                </Box>
              </Box>
              <Skeleton variant="rectangular" height={200} />
              <Box sx={{ p: 2 }}>
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="60%" />
              </Box>
            </Card>
          ))}
        </Box>
      ) : posts.length === 0 ? (
        <Box>
          {tabValue === 0 ? (
            // Following feed empty state with suggestions
            <Box>
              <Paper variant="outlined" sx={{ textAlign: 'center', py: 4, px: 2, mb: 3, borderRadius: 3 }}>
                <PersonIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No posts from people you follow yet
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Follow some users to see their posts in your feed!
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => setTabValue(1)}
                  sx={{ mt: 1 }}
                >
                  Discover Users
                </Button>
              </Paper>
              
              {/* Show suggested users */}
              <SuggestedUsers limit={8} />
            </Box>
          ) : (
            // Discover feed empty state
            <Paper variant="outlined" sx={{ textAlign: 'center', py: 8, px: 2, borderRadius: 3 }}>
              <Typography variant="h6" color="text.secondary">
                No posts to discover yet.
              </Typography>
            </Paper>
          )}
        </Box>
      ) : (
        <Box>
          {/* Show suggested users on discover tab before posts */}
          {tabValue === 1 && <SuggestedUsers limit={5} />}
          
          {/* Posts */}
          {posts.map((post) => <PostCard key={post.id} post={post} />)}
        </Box>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 20, right: 20 }}
        onClick={() => setCreatePostOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* Create Post Dialog */}
      <Dialog open={createPostOpen} onClose={() => setCreatePostOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Post</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="What's on your mind?"
            value={newPostContent}
            onChange={(e) => setNewPostContent(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          {/* Image Upload */}
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="image-upload"
            type="file"
            onChange={handleImageSelect}
          />
          <label htmlFor="image-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<ImageIcon />}
              sx={{ mb: 2 }}
            >
              Add Image
            </Button>
          </label>

          {/* Image Preview */}
          {newPostImagePreview && (
            <Box sx={{ mb: 2 }}>
              <img
                src={newPostImagePreview}
                alt="Preview"
                style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 8 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePostOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreatePost} 
            variant="contained"
            disabled={submittingPost || (!newPostContent.trim() && !newPostImage)}
          >
            {submittingPost ? <CircularProgress size={20} /> : 'Post'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SocialFeed; 
